import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { products } from '../data/mock';
import { ArrowLeft, CheckCircle2, Package, Shield, Award } from 'lucide-react';

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find((p) => p.id === parseInt(id));

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-slate-900">Product Not Found</h2>
          <Link to="/products">
            <Button className="bg-[#D8461D] hover:bg-#b83817">
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products.filter(
    (p) => p.category === product.category && p.id !== product.id
  ).slice(0, 3);

  const features = [
    'High-quality materials',
    'Durable construction',
    'Industry certified',
    'Long-lasting performance',
    'Competitive pricing',
    'Available for bulk orders'
  ];

  const specifications = [
    { label: 'Category', value: product.category },
    { label: 'Application', value: 'Industrial & Commercial' },
    { label: 'Material', value: 'Premium Grade' },
    { label: 'Certification', value: 'ISO Compliant' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back Button */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-700 hover:text-[#D8461D] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Products
          </button>
        </div>
      </div>

      {/* Product Detail Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Product Image */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl bg-white">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-[500px] object-contain bg-white p-8"
                />
                <Badge className="absolute top-6 right-6 bg-[#D8461D] text-white text-base px-4 py-2">
                  {product.category}
                </Badge>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center space-y-2">
                    <Shield size={24} className="text-[#D8461D] mx-auto" />
                    <p className="text-xs font-semibold text-slate-700">Quality Assured</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center space-y-2">
                    <Award size={24} className="text-[#D8461D] mx-auto" />
                    <p className="text-xs font-semibold text-slate-700">ISO Certified</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center space-y-2">
                    <Package size={24} className="text-[#D8461D] mx-auto" />
                    <p className="text-xs font-semibold text-slate-700">Bulk Available</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
                  {product.name}
                </h1>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 size={20} className="text-[#D8461D] flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specifications */}
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-slate-900">Specifications</h3>
                  <div className="space-y-3">
                    {specifications.map((spec, index) => (
                      <div key={index} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                        <span className="font-semibold text-slate-700">{spec.label}:</span>
                        <span className="text-slate-600">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/contact" className="flex-1">
                  <Button size="lg" className="w-full bg-[#D8461D] hover:bg-#b83817 text-white text-lg font-semibold">
                    Request Quote
                  </Button>
                </Link>
                <Link to="/contact" className="flex-1">
                  <Button size="lg" variant="outline" className="w-full border-2 border-[#D8461D] text-[#D8461D] hover:bg-orange-50 text-lg font-semibold">
                    Contact Us
                  </Button>
                </Link>
              </div>

              {/* Additional Info */}
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-6 space-y-2">
                  <p className="font-semibold text-slate-900">Need bulk quantities?</p>
                  <p className="text-sm text-slate-600">
                    We offer competitive pricing for bulk orders. Contact our sales team for volume discounts 
                    and custom manufacturing solutions.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link key={relatedProduct.id} to={`/products/${relatedProduct.id}`}>
                  <Card className="group cursor-pointer transition-all duration-300 hover:shadow-xl border-slate-200 overflow-hidden h-full">
                    <div className="relative overflow-hidden bg-white">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        className="w-full h-56 object-contain transition-transform duration-500 group-hover:scale-110 bg-white p-4"
                      />
                    </div>
                    <CardContent className="p-6 space-y-3">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#D8461D] transition-colors">
                        {relatedProduct.name}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                        {relatedProduct.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;
